<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, computed, watch, onMounted } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { TextBox, Checkbox } from '@progress/kendo-vue-inputs'

import { GetCountryCode, GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore.ts'
import { PwsBeeBankingGetList } from '../../api/Payments.ts'
import type { IBankDetails } from '../../types/WizardTypes'
import { sanitizeBankCode } from '../../validators/BankValidator.ts'

const { t } = useI18n()
const logger = useLogger()

// Function to show success messages
function showSuccessMessage(message: string) {
  // Emit an event to the parent component to show a success message
  // Since we don't have direct access to the notification system
  console.log('Success:', message)
}

// Props defined explicitly to avoid TypeScript errors
const props = defineProps({
  formData: {
    type: Object,
    required: true
  },
  onUpdate: {
    type: Function,
    required: true
  },
  isValid: {
    type: Boolean,
    required: true
  }
})

// Emit events to parent component
const emit = defineEmits(['update:isValid', 'skip-french-contribution', 'skip-to-step'])

// State for handling loading and errors
const isLoading = ref(false)
const loadError = ref('')
const countryCode = ref('')

// Default bank account type is now always IBAN

// IBAN validation based on country code
const isValidIBAN = computed(() => {
  const iban = props.formData.bankDetails.iban.trim().toUpperCase()
  if (!iban) return false
  
  // Remove spaces for validation
  const cleanIban = iban.replace(/\s/g, '')
  
  // Check IBAN format based on country code
  switch (countryCode.value) {
    case 'FR':
      // French IBAN: FR + 2 check digits + 23 characters (27 total)
      return /^FR\d{25}$/.test(cleanIban)
    case 'DE':
      // German IBAN: DE + 2 check digits + 18 characters (22 total)
      return /^DE\d{20}$/.test(cleanIban)
    case 'AT':
      // Austrian IBAN: AT + 2 check digits + 16 characters (20 total)
      return /^AT\d{18}$/.test(cleanIban)
    default:
      // For other countries, basic IBAN format check (2 letters + 2 digits + up to 30 alphanumeric)
      return /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(cleanIban)
  }
})

// Get IBAN validation message based on country
const ibanValidationMessage = computed(() => {
  const iban = props.formData.bankDetails.iban.trim()
  if (!iban) return ''
  if (isValidIBAN.value) return ''
  
  switch (countryCode.value) {
    case 'FR':
      return t('validators.ibanFormatFR')
    case 'DE':
      return t('validators.ibanFormatDE')
    case 'AT':
      return t('validators.ibanFormatAT')
    default:
      return t('validators.ibanFormat')
  }
})

// BIC validation (8 or 11 characters: 4 letters bank code + 2 letters country + 2 alphanumeric location + optional 3 alphanumeric branch)
const isValidBIC = computed(() => {
  const bic = props.formData.bankDetails.bic.trim().toUpperCase()
  if (!bic) return false
  
  // BIC format: 4 letters (bank) + 2 letters (country code: AT, FR, DE) + 2 alphanumeric (location) + optional 3 alphanumeric (branch)
  // Validate that positions 5-6 are valid country codes for Austria, France, or Germany
  return /^[A-Z]{4}(AT|FR|DE)[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic)
})

// Determine if the step is valid
const isStepValid = computed(() => {
  const { bankName, bic, iban, directDeposit } = props.formData.bankDetails
  
  // Validate required fields
  const hasRequiredFields = bankName.trim() !== '' && 
                            bic.trim() !== '' && 
                            iban.trim() !== '' &&
                            directDeposit === true
  
  // Validate IBAN and BIC formats
  const hasValidFormats = isValidIBAN.value && isValidBIC.value
  
  return hasRequiredFields && hasValidFormats
})

// Update step validity when it changes
watch(isStepValid, (newValue) => {
  emit('update:isValid', newValue)
})


// Load country code when mounting the component
onMounted(async () => {
  try {
    isLoading.value = true
    const code = await GetCountryCode()
    
    // Set bank account type as IBAN by default
    if (props.formData.bankDetails.bankAccountType === '') {
      props.onUpdate({ 
        bankDetails: { 
          ...props.formData.bankDetails, 
          bankAccountType: 'CHK' 
        } 
      })
    }
  } catch (error) {
    logger.error('Error loading country code:', error)
    loadError.value = t('errors.loadingCountryCode')
  } finally {
    isLoading.value = false
  }
})



// Handle changes in bank name
function handleBankNameChange(...args: any[]) {
  const e = args[0];
  if (e && typeof e.value === 'string') {
    props.onUpdate({ 
      bankDetails: { 
        ...props.formData.bankDetails, 
        bankName: e.value 
      } 
    })
  }
}

// Handle changes in BIC
function handleBICChange(...args: any[]) {
  const e = args[0];
  if (e && typeof e.value === 'string') {
    props.onUpdate({ 
      bankDetails: { 
        ...props.formData.bankDetails, 
        bic: sanitizeBankCode(e.value) 
      } 
    })
  }
}

// Handle changes in IBAN
function handleIBANChange(...args: any[]) {
  const e = args[0];
  if (e && typeof e.value === 'string') {
    props.onUpdate({ 
      bankDetails: { 
        ...props.formData.bankDetails, 
        iban: sanitizeBankCode(e.value) 
      } 
    })
  }
}

// Handle changes in Direct Deposit
function handleDirectDepositChange(...args: any[]) {
  const e = args[0];
  if (e) {
    props.onUpdate({ 
      bankDetails: { 
        ...props.formData.bankDetails, 
        directDeposit: e.value 
      } 
    })
  }
}

// Handle changes in OPA
function handleOPAChange(...args: any[]) {
  const e = args[0];
  if (e) {
    props.onUpdate({ 
      bankDetails: { 
        ...props.formData.bankDetails, 
        opa: e.value 
      } 
    })
  }
}
</script>

<template>
  <div class="wizard-step">
    <div class="wizard-step-header">
      <h3>{{ t('labels.BankDetailsHeader') }}</h3>
      <p>{{ t('labels.BankDetailsSubHeader') }}</p>
    </div>
    
    <div class="form-section">
      <div v-if="isLoading" class="loading-indicator">
        {{ t('labels.LoadingBankDetails') }}
      </div>
      
      <div v-else-if="loadError" class="validation-message">
        {{ loadError }}
      </div>
      
      <div v-else class="form-container">
        
        <!-- Bank Name -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.BankName') }} *</label>
          <TextBox
            v-model="formData.bankDetails.bankName"
            class="k-textbox"
            :placeholder="t('labels.BankNamePlaceholder')"
            @change="handleBankNameChange"
          />
          <!-- Removed red alert indicator -->
        </div>
        
        <!-- BIC -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.BIC') }} *</label>
          <TextBox
            v-model="formData.bankDetails.bic"
            class="k-textbox"
            :class="{ 'invalid-input': formData.bankDetails.bic !== '' && !isValidBIC }"
            :placeholder="t('labels.BICPlaceholder')"
            @change="handleBICChange"
          />
          <div v-if="formData.bankDetails.bic !== '' && !isValidBIC" class="validation-message">
            {{ t('validators.bicFormat') }}
          </div>
        </div>
        
        <!-- IBAN -->
        <div class="form-row">
          <label class="form-label">{{ t('labels.IBAN') }} *</label>
          <TextBox
            v-model="formData.bankDetails.iban"
            class="k-textbox"
            :class="{ 'invalid-input': formData.bankDetails.iban !== '' && !isValidIBAN }"
            :placeholder="t('labels.IBANPlaceholder')"
            @change="handleIBANChange"
          />
          <div v-if="ibanValidationMessage" class="validation-message">
            {{ ibanValidationMessage }}
          </div>
        </div>
        
        <!-- Direct Deposit -->
        <div class="form-row checkbox-row">
          <Checkbox
            v-model="formData.bankDetails.directDeposit"
            :label="t('labels.DirectDeposit')"
            @change="handleDirectDepositChange"
          />
        </div>
        
        <!-- OPA -->
        <div class="form-row checkbox-row">
          <Checkbox
            v-model="formData.bankDetails.opa"
            :label="t('labels.OPA')"
            @change="handleOPAChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wizard-step {
  margin-bottom: 25px;
}

.wizard-step-header {
  margin-bottom: 16px;
}

.wizard-step-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 6px;
}

.wizard-step-header p {
  @include typography-body;
  color: #666;
  margin-bottom: 10px;
}

.form-section {
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
}

.form-row {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  max-width: 600px;
  width: 100%;
}

.checkbox-row {
  display: flex;
  align-items: center;
}

.form-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  display: block;
  margin-bottom: 6px;
  min-width: 120px;
}

:deep(.k-checkbox-label) {
  @include typography-body;
}

:deep(.k-textbox) {
  @include typography-body;
  width: 100%;
}

.validation-message {
  @include typography-small;
  color: #e53935;
  margin-top: 4px;
  text-align: center;
}

.loading-indicator {
  margin: $spacing-4 0;
  font-style: italic;
  @include typography-body;
  color: #666;
  text-align: center;
}

/* Style for invalid input */
.invalid-input {
  border-color: #e53935;
}

:deep(.k-textbox.invalid-input) {
  border-color: #e53935;
}


</style>
